import { useEffect, useState } from 'react';
import { Check, Receipt, Sparkles } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';

type Summary = { policy_type: string; deductible: string; limits: string; building_code: string; clauses: string[] };
const EMPTY: Summary = { policy_type: '', deductible: '', limits: '', building_code: '', clauses: [] };

/**
 * Resumen de póliza para el card del claim: saca de la lectura de la póliza los
 * valores clave (tipo, deducible, límites, building code, cláusulas importantes),
 * el usuario los confirma/edita y los manda al card (claims.policy_summary).
 */
export function ClaimPolicySummary({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [form, setForm] = useState<Summary>(EMPTY);
  const [suggestion, setSuggestion] = useState<Summary | null>(null);
  const [saved, setSaved] = useState<Summary | null>(null);
  const [hasPolicy, setHasPolicy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  const { fallo, marcar, limpiar } = useFallo();
  const load = () => api.get<{ saved: Summary | null; suggestion: Summary | null; hasPolicy: boolean }>(`/api/policy-reader/claims/${claimId}/summary`)
    .then((r) => {
      setSaved(r.saved); setSuggestion(r.suggestion); setHasPolicy(r.hasPolicy);
      setForm(r.saved ?? r.suggestion ?? EMPTY);
    }).then(limpiar).catch(marcar).finally(() => setLoaded(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId]);

  const set = (k: keyof Summary, v: string) => { setForm((f) => ({ ...f, [k]: v })); setOk(false); };
  const useSuggestion = () => { if (suggestion) { setForm(suggestion); setOk(false); } };

  async function save() {
    setBusy(true); setOk(false);
    try { await api.put(`/api/policy-reader/claims/${claimId}/summary`, form); setOk(true); await load(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  if (!loaded) return null;

  const field = (label: string, k: keyof Summary) => (
    <div style={{ display: 'grid', gap: 3 }}>
      <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{label}</span>
      <input className="input sm" value={(form[k] as string) || ''} onChange={(e) => set(k, e.target.value)} />
    </div>
  );

  return (
    <div className="card" style={{ display: 'grid', gap: 10 }}>
      {fallo && <NoCargo onRetry={load} compact />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h2 className="card-title" style={{ margin: 0 }}><Receipt size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('psum.title')}</h2>
        {suggestion && <button className="btn ghost sm" onClick={useSuggestion}><Sparkles size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('psum.useSuggestion')}</button>}
      </div>
      {!hasPolicy && <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('psum.noPolicy')}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        {field(t('psum.policyType'), 'policy_type')}
        {field(t('psum.deductible'), 'deductible')}
        {field(t('psum.limits'), 'limits')}
        {field(t('psum.buildingCode'), 'building_code')}
      </div>
      <div style={{ display: 'grid', gap: 3 }}>
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('psum.clauses')}</span>
        <textarea className="input" rows={4} style={{ resize: 'vertical', font: 'inherit' }}
          value={(form.clauses || []).join('\n')} placeholder={t('psum.clausesHint')}
          onChange={(e) => { setForm((f) => ({ ...f, clauses: e.target.value.split('\n') })); setOk(false); }} />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn sm" disabled={busy} onClick={save}><Check size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('psum.confirm')}</button>
        {ok && <span style={{ color: 'var(--green)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)' }}>{t('psum.saved')}</span>}
        {saved && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('psum.onCard')}</span>}
      </div>
    </div>
  );
}
